/* ============================================================================
 * Window manager — z-index layering state machine
 * ----------------------------------------------------------------------------
 * Pure state + reducers. The shell renders from this; nothing else owns window
 * geometry. z-order is a monotonically increasing counter so focus is a single
 * assignment and never a re-sort.
 * ========================================================================= */

import { useCallback, useMemo, useRef, useState } from 'react';

export interface WindowGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface OSWindowState extends WindowGeometry {
  /** Unique instance id (an app may be open more than once, in principle). */
  id: string;
  appId: string;
  title: string;
  z: number;
  minimized: boolean;
}

export interface OpenArgs extends Partial<WindowGeometry> {
  appId: string;
  title: string;
  id?: string;
}

export interface WindowManager {
  windows: OSWindowState[];
  activeId: string | null;
  open: (args: OpenArgs) => string;
  close: (id: string) => void;
  focus: (id: string) => void;
  minimize: (id: string) => void;
  restore: (id: string) => void;
  toggleMinimize: (id: string) => void;
  move: (id: string, x: number, y: number) => void;
  resize: (id: string, w: number, h: number) => void;
  cascade: () => { x: number; y: number };
  isOpen: (appId: string) => boolean;
  windowFor: (appId: string) => OSWindowState | undefined;
}

const CASCADE_STEP = 26;
const ORIGIN = { x: 108, y: 44 };

export function useWindowManager(): WindowManager {
  const [windows, setWindows] = useState<OSWindowState[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const zRef = useRef(10);
  const cascadeRef = useRef(0);
  const seqRef = useRef(0);

  const nextZ = useCallback(() => {
    zRef.current += 1;
    return zRef.current;
  }, []);

  const cascade = useCallback(() => {
    const i = cascadeRef.current++;
    return {
      x: ORIGIN.x + (i % 7) * CASCADE_STEP,
      y: ORIGIN.y + (i % 7) * CASCADE_STEP,
    };
  }, []);

  const open = useCallback(
    (args: OpenArgs) => {
      const id = args.id ?? `${args.appId}#${++seqRef.current}`;
      setWindows((prev) => {
        const existing = prev.find((w) => w.id === id);
        if (existing) {
          return prev.map((w) =>
            w.id === id ? { ...w, minimized: false, z: nextZ() } : w,
          );
        }
        const spot = args.x !== undefined && args.y !== undefined
          ? { x: args.x, y: args.y }
          : cascade();
        return [
          ...prev,
          {
            id,
            appId: args.appId,
            title: args.title,
            x: spot.x,
            y: spot.y,
            w: args.w ?? 480,
            h: args.h ?? 360,
            z: nextZ(),
            minimized: false,
          },
        ];
      });
      setActiveId(id);
      return id;
    },
    [cascade, nextZ],
  );

  const close = useCallback((id: string) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
    setActiveId((cur) => (cur === id ? null : cur));
  }, []);

  const focus = useCallback(
    (id: string) => {
      setWindows((prev) =>
        prev.map((w) =>
          w.id === id ? { ...w, z: nextZ(), minimized: false } : w,
        ),
      );
      setActiveId(id);
    },
    [nextZ],
  );

  const minimize = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, minimized: true } : w)),
    );
    setActiveId((cur) => (cur === id ? null : cur));
  }, []);

  const restore = useCallback(
    (id: string) => {
      focus(id);
    },
    [focus],
  );

  const toggleMinimize = useCallback(
    (id: string) => {
      setWindows((prev) => {
        const w = prev.find((x) => x.id === id);
        if (!w) return prev;
        if (w.minimized) {
          // restoring
          return prev.map((x) =>
            x.id === id ? { ...x, minimized: false, z: nextZ() } : x,
          );
        }
        return prev.map((x) => (x.id === id ? { ...x, minimized: true } : x));
      });
      setActiveId((cur) => {
        const w = windows.find((x) => x.id === id);
        if (!w) return cur;
        return w.minimized ? id : cur === id ? null : cur;
      });
    },
    [nextZ, windows],
  );

  const move = useCallback((id: string, x: number, y: number) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, x, y } : w)));
  }, []);

  const resize = useCallback((id: string, w0: number, h0: number) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, w: Math.max(260, w0), h: Math.max(140, h0) }
          : w,
      ),
    );
  }, []);

  const api = useMemo<WindowManager>(
    () => ({
      windows,
      activeId,
      open,
      close,
      focus,
      minimize,
      restore,
      toggleMinimize,
      move,
      resize,
      cascade,
      isOpen: (appId: string) =>
        windows.some((w) => w.appId === appId && !w.minimized),
      windowFor: (appId: string) => windows.find((w) => w.appId === appId),
    }),
    [
      windows,
      activeId,
      open,
      close,
      focus,
      minimize,
      restore,
      toggleMinimize,
      move,
      resize,
      cascade,
    ],
  );

  return api;
}
